import { classNameMerger } from '@breetometer/utils';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { ITableTooltip, TooltipPosition } from './Tooltip.helpers';

const TableTooltip = ({
	children,
	message = 'Click to view details',
	enabled = true,
	truncate,
	as: Tag = 'span',
	className,
	style,
	onClick,
}: ITableTooltip) => {
	const [position, setPosition] = useState<TooltipPosition | null>(null);
	const wrapRef = useRef<HTMLElement>(null);

	const handleMouseEnter = useCallback(() => {
		const el = wrapRef.current;
		if (!el) return;

		// For truncation tooltips, only reveal the bubble when the text is actually clipped — i.e. its
		// rendered width exceeds the container. Affordance tooltips (no `truncate`) still always show.
		if (truncate && el.scrollWidth <= el.clientWidth) return;

		const rect = el.getBoundingClientRect();
		setPosition({
			top: rect.top - 8,
			left: rect.left + rect.width / 2,
		});
	}, [truncate]);

	const handleMouseLeave = useCallback(() => {
		setPosition(null);
	}, []);

	if (!enabled && Tag !== 'td') return <>{children}</>;

	return (
		<Tag
			ref={wrapRef}
			className={className ?? classNameMerger('table-row-tooltip', truncate && 'truncate')}
			style={style}
			onClick={onClick}
			onMouseEnter={enabled ? handleMouseEnter : undefined}
			onMouseLeave={enabled ? handleMouseLeave : undefined}
		>
			{children}

			{enabled &&
				position &&
				createPortal(
					<div
						className='table-row-tooltip__bubble hidden'
						style={{
							top: position.top,
							left: position.left,
						}}
					>
						{message}
					</div>,
					document.body
				)}
		</Tag>
	);
};

export default TableTooltip;
